import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import {
  THEME_MODES,
  type ThemeMode,
  useThemeStore,
} from '../stores/themeStore';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const LABELS: Record<ThemeMode, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};

const ICONS: Record<ThemeMode, IconComponent> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

function isThemeMode(value: string): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function ThemeToggle(): React.JSX.Element {
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  return (
    <ToggleGroup.Root
      type="single"
      value={mode}
      onValueChange={(next) => {
        // Radix emits '' if the user tries to deselect the active item.
        // The theme is always set to one of the three modes — ignore.
        if (next !== '' && isThemeMode(next)) setMode(next);
      }}
      aria-label="Theme"
      className="border-border bg-bg-elevated inline-flex rounded-2 border p-1 gap-1"
    >
      {THEME_MODES.map((m) => {
        const Icon = ICONS[m];
        return (
          <ToggleGroup.Item
            key={m}
            value={m}
            aria-label={LABELS[m]}
            className="text-text-muted hover:text-text data-[state=on]:bg-accent data-[state=on]:text-accent-fg flex items-center gap-2 rounded-2 px-3 py-2 font-mono text-11 transition-colors"
          >
            <Icon width={12} height={12} strokeWidth={1.5} aria-hidden="true" />
            {LABELS[m]}
          </ToggleGroup.Item>
        );
      })}
    </ToggleGroup.Root>
  );
}
